import { BoxSlotTypeOf } from "@/modules/boxSlot/boxSlotTypes"
import { HUImageDtoSchema } from "@/modules/dtos/imageDto"
import { SlotParticipantStatus } from "@/modules/profile/subscriber/gateway/subscriberDtos"
import { UserDtoSchema } from "@/modules/profile/userProfile/userDto"
import { ProgramType, ProgramVisibility } from "@/modules/programs/types/programTypes"
import { SessionTypeOf } from "@/modules/sessions/programSessionTypes"
import { z } from "zod"

export const MobileSessionItemDtoSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    description: z.string().nullable(),
    date: z.string().nullable(),
    owner: UserDtoSchema.nullable(),
    programId: z.string(),
    programName: z.string(),
    programVisibility: z.enum(ProgramVisibility),
    programType: z.literal(ProgramType.VIRTUAL).or(z.literal(ProgramType.PRO)),
    hideSessionContent: z.boolean().nullable(),
    weightliftingTag: z.boolean(),
    gymnasticTag: z.boolean(),
    cardioTag: z.boolean(),
    restDay: z.boolean(),
    index: z.number().nullable(),
    virtualProgramByDateIndex: z.number().nullable(),
  })
  .meta({ id: "MobileSessionItemDtoSchema" })

export const OnOffSessionItemDtoSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    description: z.string().nullable(),
    date: z.string().nullable(),
    owner: UserDtoSchema,
    programId: z.string(),
    programName: z.string(),
    programVisibility: z.enum(ProgramVisibility),
    programType: z.literal(ProgramType.ONOFF),
    hideSessionContent: z.boolean().nullable(),
    weightliftingTag: z.boolean(),
    gymnasticTag: z.boolean(),
    cardioTag: z.boolean(),
    restDay: z.boolean(),
    index: z.number().nullable(),
  })
  .meta({ id: "OnOffSessionItemDtoSchema" })

const MobileBoxProgramRefDto = z.object({
  id: z.string(),
  name: z.string(),
  image: HUImageDtoSchema,
  type: z.enum(ProgramType),
})
const MobileBoxRefDto = z.object({
  id: z.string(),
  name: z.string(),
  event: z.boolean(),
  showSessionAttendees: z.boolean(),
})

export const MobileSlotItemDtoSchema = z
  .object({
    id: z.string(),
    startDate: z.string(),
    endDate: z.string(),
    program: MobileBoxProgramRefDto,
    box: MobileBoxRefDto,
    hasSession: z.boolean(),
    hideSessionContent: z.boolean().nullable(),
    owner: UserDtoSchema,
    participantLimit: z.number().nullable(),
    registeredParticipantCount: z.number(),
    pendingParticipantCount: z.number(),
    participantStatus: z.enum(SlotParticipantStatus),
    location: z.string().nullable(),
    dropinsAccepted: z.boolean(),
    trialsAccepted: z.boolean(),
    waitingListPosition: z.number().nullable(),
    participantDropinsCount: z.number().nullable(),
    participantTrialCount: z.number().nullable(),
    guests: z.array(UserDtoSchema),
    assignedStaffMembers: z.array(UserDtoSchema),
    participantCount: z.number(), // deprecated
    registered: z.boolean(), // deprecated
  })
  .meta({ id: "MobileSlotItemDtoSchema" })

export const TrainingItemDtoSchema = z
  .object({
    type: z.literal(BoxSlotTypeOf),
    content: MobileSlotItemDtoSchema,
  })
  .or(
    z
      .object({
        type: z.literal(SessionTypeOf),
        content: MobileSessionItemDtoSchema,
      })
      .or(
        z.object({
          type: z.literal(SessionTypeOf),
          content: OnOffSessionItemDtoSchema,
        }),
      ),
  )
  .meta({ id: "TrainingItemDtoSchema" })

/* DTO object returned by :
 * GET /web/trainings
 */
export type MobileSlotItemDto = z.infer<typeof MobileSlotItemDtoSchema>
export type OnOffSessionItemDto = z.infer<typeof OnOffSessionItemDtoSchema>
export type MobileSessionItemDto = z.infer<typeof MobileSessionItemDtoSchema>
export type TrainingItemDto = z.infer<typeof TrainingItemDtoSchema>
